import React, { useState, useEffect } from 'react';
import { Users, Calendar, BarChart3, ChevronRight, Eye, Save, Loader, Plus, X } from 'lucide-react';
import { db } from '../firebase';
import { 
  collection, 
  getDocs, 
  doc, 
  setDoc, 
  getDoc,
  query,
  where 
} from 'firebase/firestore';
import { useOrganization } from '../contexts/OrganizationContext';
import { OrganizationService } from '../services/organizationService';
import dayjs from 'dayjs';

const skillLevels = [
  "未経験", "見学済み", "経験1回", "経験2回",
  "経験3回以上", "外から見守り", "独り立ち", "指導可"
];

const skillScoreMap = {
  "未経験": 0, "見学済み": 1, "経験1回": 2, "経験2回": 3,
  "経験3回以上": 4, "外から見守り": 5, "独り立ち": 6, "指導可": 7
};

const StaffAssignmentEnhanced = () => {
  const { currentOrganization } = useOrganization();
  const [currentPage, setCurrentPage] = useState('schedule');
  
  // データ
  const [procedures, setProcedures] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [surgeries, setSurgeries] = useState([]);
  const [skillRecords, setSkillRecords] = useState([]);
  const [trainingPlans, setTrainingPlans] = useState({});
  
  // UI状態
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedSurgery, setSelectedSurgery] = useState(null);
  const [selectionMode, setSelectionMode] = useState(null);
  const [showSkillChart, setShowSkillChart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedProcedureForTraining, setSelectedProcedureForTraining] = useState(null);
  const [selectedRoleForTraining, setSelectedRoleForTraining] = useState(null);
  const [masterData, setMasterData] = useState([]);
  const [calculating, setCalculating] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // データロード
  useEffect(() => {
    if (currentOrganization?.id) {
      loadAllData();
    }
  }, [currentOrganization]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      console.log('🔄 データ読み込み開始...', currentOrganization.id);
      
      // 術式マスタ取得
      const procsCollection = OrganizationService.getOrgCollection(
        currentOrganization.id,
        "procedures"
      );
      const procsSnap = await getDocs(procsCollection);
      const procsData = procsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('✅ 術式データ:', procsData.length, '件');
      setProcedures(procsData);

      // スタッフ取得
      const staffCollection = collection(db, `organizations/${currentOrganization.id}/staffList`);
      const staffSnap = await getDocs(staffCollection);
      const staffData = staffSnap.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          displayName: `${data.lastName || ''} ${data.firstName || ''}`.trim() || data.name || 'Unknown'
        };
      });
      console.log('✅ スタッフデータ:', staffData.length, '人');
      setStaffList(staffData);

      // スキルレコードは後で必要に応じて読み込む
      console.log('⏭️ スキルレコードは遅延読み込みします');
      setSkillRecords([]);

      // 当日の手術スケジュール取得
      await loadSurgeries(selectedDate.format('YYYY-MM-DD'));

      // 育成方針取得
      await loadTrainingPlans();

    } catch (error) {
      console.error('❌ データ読み込みエラー:', error);
      alert('データの読み込みに失敗しました: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadSkillRecords = async () => {
    if (skillRecords.length > 0) {
      console.log('✅ スキルレコードは既に読み込み済み');
      return;
    }
    
    try {
      console.log('🔄 スキルレコード読み込み開始...');
      const skillCollection = collection(db, `organizations/${currentOrganization.id}/skillRecords`);
      const skillSnap = await getDocs(skillCollection);
      const skillData = skillSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('✅ スキルレコード:', skillData.length, '件');
      setSkillRecords(skillData);
    } catch (error) {
      console.error('❌ スキルレコード読み込みエラー:', error);
    }
  };

  const loadSurgeries = async (date) => {
    try {
      const surgeriesCollection = OrganizationService.getOrgCollection(
        currentOrganization.id,
        "surgerySchedules"
      );
      const q = query(surgeriesCollection, where("date", "==", date));
      const snapshot = await getDocs(q);
      const surgeriesData = snapshot.docs.map(doc => ({
        id: doc.id,
        docId: doc.id,
        ...doc.data()
      }));
      console.log('✅ 手術スケジュール:', surgeriesData.length, '件');
      setSurgeries(surgeriesData);
    } catch (error) {
      console.error('❌ 手術スケジュール読み込みエラー:', error);
    }
  };

  const loadTrainingPlans = async () => {
    try {
      const planDoc = await getDoc(
        doc(db, `organizations/${currentOrganization.id}/trainingPlans/master`)
      );
      if (planDoc.exists()) {
        setTrainingPlans(planDoc.data().plans || {});
        console.log('✅ 育成方針:', planDoc.data().plans);
      }
    } catch (error) {
      console.error('❌ 育成方針読み込みエラー:', error);
    }
  };

  const saveTrainingPlans = async () => {
    try {
      setSaving(true);
      await setDoc(
        doc(db, `organizations/${currentOrganization.id}/trainingPlans/master`),
        {
          plans: trainingPlans,
          updatedAt: new Date()
        }
      );
      alert('育成方針を保存しました');
    } catch (error) {
      console.error('❌ 保存エラー:', error);
      alert('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const getStaffSkillLevel = (staffId, procedureId, role) => {
    const record = skillRecords.find(
      r => r.userId === staffId && 
           r.procedureId === procedureId && 
           r.role === role
    );
    return record ? record.level : "未経験";
  };

  const getStaffExperienceCount = (staffId, procedureId, role) => {
    const record = skillRecords.find(
      r => r.userId === staffId && 
           r.procedureId === procedureId && 
           r.role === role
    );
    return record?.count || 0;
  };

  const getLastExperienceDate = (staffId, procedureId) => {
    return '2025-10-20';
  };

  // スキルチャートモーダル
  const SkillChartModal = ({ staff }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" 
         onClick={() => setShowSkillChart(null)}>
      <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto" 
           onClick={e => e.stopPropagation()}>
        <h3 className="text-xl font-bold mb-4">{staff.displayName} のスキル</h3>
        
        <div className="space-y-4">
          {procedures.map(proc => {
            const scrubSkill = skillRecords.find(
              r => r.userId === staff.id && 
                   r.procedureId === proc.id && 
                   r.role === 'scrub'
            );
            const circSkill = skillRecords.find(
              r => r.userId === staff.id && 
                   r.procedureId === proc.id && 
                   r.role === 'circulating'
            );
            
            if (!scrubSkill && !circSkill) return null;
            
            return (
              <div key={proc.id} className="border-b pb-3">
                <div className="font-semibold text-sm mb-2">{proc.name}</div>
                {scrubSkill && (
                  <div className="text-sm text-gray-600 ml-2">
                    器械: {scrubSkill.level} ({scrubSkill.count || 0}回)
                  </div>
                )}
                {circSkill && (
                  <div className="text-sm text-gray-600 ml-2">
                    外回り: {circSkill.level} ({circSkill.count || 0}回)
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        <button 
          onClick={() => setShowSkillChart(null)}
          className="mt-6 w-full bg-gray-200 hover:bg-gray-300 py-2 rounded"
        >
          閉じる
        </button>
      </div>
    </div>
  );

  // 育成対象者選択モーダル
  const TraineeSelectionModal = ({ procedure, role, onClose }) => {
    const roleLabel = role === 'scrub' ? '器械出し' : '外回り';
    const currentTrainees = trainingPlans[procedure.id]?.[role] || [];
    
    const trainableStaff = staffList.filter(staff => {
      const level = getStaffSkillLevel(staff.id, procedure.id, role);
      const score = skillScoreMap[level];
      return score < 6;
    });

    const toggleStaff = (staffId) => {
      const newPlans = { ...trainingPlans };
      if (!newPlans[procedure.id]) {
        newPlans[procedure.id] = { scrub: [], circulating: [] };
      }
      
      const current = newPlans[procedure.id][role] || [];
      
      if (current.includes(staffId)) {
        newPlans[procedure.id][role] = current.filter(id => id !== staffId);
      } else {
        newPlans[procedure.id][role] = [...current, staffId];
      }
      
      setTrainingPlans(newPlans);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" 
           onClick={onClose}>
        <div className="bg-white p-6 rounded-lg max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto" 
             onClick={e => e.stopPropagation()}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold">{procedure.name} - {roleLabel} 育成対象者選択</h3>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded">
              <X size={20} />
            </button>
          </div>
          
          {trainableStaff.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              育成可能なスタッフがいません
            </div>
          ) : (
            <div className="space-y-2">
              {trainableStaff.map(staff => {
                const level = getStaffSkillLevel(staff.id, procedure.id, role);
                const isSelected = currentTrainees.includes(staff.id);
                
                return (
                  <div 
                    key={staff.id}
                    onClick={() => toggleStaff(staff.id)}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      isSelected ? 'bg-blue-50 border-blue-300' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        readOnly
                        className="w-5 h-5"
                      />
                      <div className="flex-1">
                        <div className="font-semibold">{staff.displayName}</div>
                        <div className="text-sm text-gray-600">現在: {level}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          <div className="mt-6 flex gap-3">
            <button 
              onClick={onClose}
              className="flex-1 bg-gray-200 hover:bg-gray-300 py-2 rounded"
            >
              閉じる
            </button>
            <button 
              onClick={() => {
                saveTrainingPlans();
                onClose();
              }}
              className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 rounded flex items-center justify-center gap-2"
            >
              <Save size={18} />
              保存して閉じる
            </button>
          </div>
        </div>
      </div>
    );
  };

  // 育成方針ページ
  const TrainingPlanPage = () => {
    useEffect(() => {
      if (currentPage === 'training' && skillRecords.length === 0) {
        loadSkillRecords();
      }
    }, [currentPage]);

    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">育成方針設定</h2>
          <button
            onClick={saveTrainingPlans}
            disabled={saving}
            className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-50"
          >
            {saving ? <Loader className="animate-spin" size={20} /> : <Save size={20} />}
            保存
          </button>
        </div>
        
        <div className="space-y-4">
          {procedures.map(proc => {
            const scrubTrainees = trainingPlans[proc.id]?.scrub || [];
            const circTrainees = trainingPlans[proc.id]?.circulating || [];
            
            return (
              <div key={proc.id} className="bg-white p-4 rounded-lg shadow">
                <h3 className="font-semibold mb-3 text-lg">{proc.name}</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="border-r pr-4">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-blue-700">器械出し</h4>
                      <button
                        onClick={() => {
                          setSelectedProcedureForTraining(proc);
                          setSelectedRoleForTraining('scrub');
                        }}
                        className="flex items-center gap-1 bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-sm"
                      >
                        <Plus size={14} />
                        選択
                      </button>
                    </div>
                    
                    {scrubTrainees.length === 0 ? (
                      <div className="text-sm text-gray-500 p-2 bg-gray-50 rounded">未設定</div>
                    ) : (
                      <div className="space-y-1">
                        {scrubTrainees.map(staffId => {
                          const staff = staffList.find(s => s.id === staffId);
                          if (!staff) return null;
                          const level = getStaffSkillLevel(staffId, proc.id, 'scrub');
                          
                          return (
                            <div key={staffId} className="flex items-center justify-between p-2 bg-blue-50 rounded text-sm">
                              <div>
                                <div className="font-medium">{staff.displayName}</div>
                                <div className="text-xs text-gray-600">{level}</div>
                              </div>
                              <button
                                onClick={() => {
                                  const newPlans = { ...trainingPlans };
                                  if (!newPlans[proc.id]) newPlans[proc.id] = { scrub: [], circulating: [] };
                                  newPlans[proc.id].scrub = (newPlans[proc.id].scrub || []).filter(id => id !== staffId);
                                  setTrainingPlans(newPlans);
                                }}
                                className="p-1 hover:bg-red-100 rounded"
                              >
                                <X size={14} className="text-red-500" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  
                  <div className="pl-4">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-green-700">外回り</h4>
                      <button
                        onClick={() => {
                          setSelectedProcedureForTraining(proc);
                          setSelectedRoleForTraining('circulating');
                        }}
                        className="flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded text-sm"
                      >
                        <Plus size={14} />
                        選択
                      </button>
                    </div>
                    
                    {circTrainees.length === 0 ? (
                      <div className="text-sm text-gray-500 p-2 bg-gray-50 rounded">未設定</div>
                    ) : (
                      <div className="space-y-1">
                        {circTrainees.map(staffId => {
                          const staff = staffList.find(s => s.id === staffId);
                          if (!staff) return null;
                          const level = getStaffSkillLevel(staffId, proc.id, 'circulating');
                          
                          return (
                            <div key={staffId} className="flex items-center justify-between p-2 bg-green-50 rounded text-sm">
                              <div>
                                <div className="font-medium">{staff.displayName}</div>
                                <div className="text-xs text-gray-600">{level}</div>
                              </div>
                              <button
                                onClick={() => {
                                  const newPlans = { ...trainingPlans };
                                  if (!newPlans[proc.id]) newPlans[proc.id] = { scrub: [], circulating: [] };
                                  newPlans[proc.id].circulating = (newPlans[proc.id].circulating || []).filter(id => id !== staffId);
                                  setTrainingPlans(newPlans);
                                }}
                                className="p-1 hover:bg-red-100 rounded"
                              >
                                <X size={14} className="text-red-500" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // スタッフ選択モーダル
  const StaffSelectionModal = () => {
    if (!selectedSurgery || !selectionMode) return null;
    
    const [role, type] = selectionMode.split('-');
    const roleLabel = role === 'scrub' ? '器械出し' : '外回り';
    const typeLabel = type === 'independent' ? '自立者' : '育成者';
    
    const procedure = procedures.find(p => p.name === selectedSurgery.procedure);
    if (!procedure) return null;
    
    let availableStaff = [];
    
    if (type === 'independent') {
      availableStaff = staffList.filter(staff => {
        const level = getStaffSkillLevel(staff.id, procedure.id, role);
        const score = skillScoreMap[level];
        return score >= 6;
      });
    } else {
      const traineeIds = trainingPlans[procedure.id]?.[role] || [];
      availableStaff = staffList.filter(staff => traineeIds.includes(staff.id));
    }

    const handleSelectStaff = async (staff) => {
      try {
        const updates = {
          [`${role === 'scrub' ? 'scrub' : 'circulating'}`]: {
            id: staff.id,
            name: staff.displayName,
            employeeId: staff.employeeId
          },
          updatedAt: new Date()
        };
        
        await setDoc(
          doc(db, `organizations/${currentOrganization.id}/surgerySchedules/${selectedSurgery.docId}`),
          updates,
          { merge: true }
        );
        
        alert(`${staff.displayName}を${roleLabel}に配置しました`);
        setSelectionMode(null);
        await loadSurgeries(selectedDate.format('YYYY-MM-DD'));
      } catch (error) {
        console.error('配置エラー:', error);
        alert('配置に失敗しました');
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40" 
           onClick={() => setSelectionMode(null)}>
        <div className="bg-white p-6 rounded-lg max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto" 
             onClick={e => e.stopPropagation()}>
          <h3 className="text-xl font-bold mb-4">
            {selectedSurgery.procedure} - {roleLabel}({typeLabel})選択
          </h3>
          
          {availableStaff.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              該当するスタッフがいません
            </div>
          ) : (
            <div className="space-y-3">
              {availableStaff.map(staff => {
                const level = getStaffSkillLevel(staff.id, procedure.id, role);
                const count = getStaffExperienceCount(staff.id, procedure.id, role);
                const lastDate = getLastExperienceDate(staff.id, procedure.id);
                const levelScore = skillScoreMap[level];
                
                return (
                  <div 
                    key={staff.id}
                    className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleSelectStaff(staff)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-semibold text-lg">{staff.displayName}</span>
                          <span className={`px-2 py-1 text-xs rounded ${
                            levelScore >= 7 ? 'bg-green-100 text-green-800' :
                            levelScore >= 6 ? 'bg-blue-100 text-blue-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {level}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 space-y-1">
                          <div>累計: {count}回</div>
                          <div>最終: {lastDate}</div>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowSkillChart(staff);
                        }}
                        className="p-2 hover:bg-gray-200 rounded"
                      >
                        <Eye size={20} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          <button 
            onClick={() => setSelectionMode(null)}
            className="mt-6 w-full bg-gray-200 hover:bg-gray-300 py-2 rounded"
          >
            キャンセル
          </button>
        </div>
      </div>
    );
  };

  // 人員配置画面
  const SchedulePage = () => {
    useEffect(() => {
      if (currentPage === 'schedule' && skillRecords.length === 0) {
        loadSkillRecords();
      }
    }, [currentPage]);

    return (
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-4">手術スケジュール</h2>
        <input
          type="date"
          value={selectedDate.format('YYYY-MM-DD')}
          onChange={(e) => {
            const newDate = dayjs(e.target.value);
            setSelectedDate(newDate);
            loadSurgeries(newDate.format('YYYY-MM-DD'));
          }}
          className="mb-6 px-4 py-2 border rounded"
        />
        
        <div className="space-y-4">
          {surgeries.map(surgery => (
            <div key={surgery.id} className="bg-white rounded-lg shadow">
              <div 
                className="p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => setSelectedSurgery(
                  selectedSurgery?.id === surgery.id ? null : surgery
                )}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-sm text-gray-500">{surgery.start} - {surgery.end}</div>
                    <div className="font-semibold text-lg">{surgery.procedure}</div>
                    <div className="text-sm text-gray-600">
                      {surgery.department} / {surgery.surgeon}
                    </div>
                  </div>
                  <ChevronRight className={`transform transition-transform ${
                    selectedSurgery?.id === surgery.id ? 'rotate-90' : ''
                  }`} />
                </div>
              </div>
              
              {selectedSurgery?.id === surgery.id && (
                <div className="border-t p-4 bg-gray-50">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold mb-2">器械出し</h4>
                      <div className="space-y-2">
                        <button
                          onClick={() => setSelectionMode('scrub-independent')}
                          className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 rounded"
                        >
                          自立者を選ぶ
                        </button>
                        <button
                          onClick={() => setSelectionMode('scrub-trainee')}
                          className="w-full bg-green-500 hover:bg-green-600 text-white py-2 rounded"
                        >
                          育成者を選ぶ
                        </button>
                        {surgery.scrub && (
                          <div className="mt-2 p-2 bg-white rounded border">
                            選択中: {surgery.scrub.name}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold mb-2">外回り</h4>
                      <div className="space-y-2">
                        <button
                          onClick={() => setSelectionMode('circulating-independent')}
                          className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 rounded"
                        >
                          自立者を選ぶ
                        </button>
                        <button
                          onClick={() => setSelectionMode('circulating-trainee')}
                          className="w-full bg-green-500 hover:bg-green-600 text-white py-2 rounded"
                        >
                          育成者を選ぶ
                        </button>
                        {surgery.circulating && (
                          <div className="mt-2 p-2 bg-white rounded border">
                            選択中: {surgery.circulating.name}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
          
          {surgeries.length === 0 && !loading && (
            <div className="text-center py-8 text-gray-500">
              この日の手術予定はありません
            </div>
          )}
        </div>
      </div>
    );
  };

  // マスター画面
  const MasterPage = () => {
    const handleSort = (key) => {
      let direction = 'asc';
      if (sortConfig.key === key && sortConfig.direction === 'asc') {
        direction = 'desc';
      }
      setSortConfig({ key, direction });
    };

    const getSortedData = () => {
      if (!sortConfig.key) return masterData;

      const sorted = [...masterData].sort((a, b) => {
        let aVal, bVal;

        switch (sortConfig.key) {
          case 'scrubInstructors':
            aVal = a.scrubInstructors;
            bVal = b.scrubInstructors;
            break;
          case 'scrubIndependent':
            aVal = a.scrubIndependent;
            bVal = b.scrubIndependent;
            break;
          case 'circInstructors':
            aVal = a.circInstructors;
            bVal = b.circInstructors;
            break;
          case 'circIndependent':
            aVal = a.circIndependent;
            bVal = b.circIndependent;
            break;
          case 'department':
            aVal = a.department || '';
            bVal = b.department || '';
            return sortConfig.direction === 'asc' 
              ? aVal.localeCompare(bVal, 'ja')
              : bVal.localeCompare(aVal, 'ja');
          default:
            return 0;
        }

        if (sortConfig.direction === 'asc') {
          return aVal - bVal;
        } else {
          return bVal - aVal;
        }
      });

      return sorted;
    };

    const SortButton = ({ column, label }) => (
      <button
        onClick={() => handleSort(column)}
        className="flex items-center gap-1 hover:bg-gray-200 px-2 py-1 rounded transition-colors"
      >
        <span>{label}</span>
        {sortConfig.key === column && (
          <span className="text-blue-600">
            {sortConfig.direction === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </button>
    );

    useEffect(() => {
      const calculateMasterData = async () => {
        // 既に計算済みなら何もしない
        if (masterData.length > 0 && currentPage === 'master') {
          return;
        }

        console.log('📊 マスターデータ計算開始...');
        setCalculating(true);
        
        if (skillRecords.length === 0) {
          await loadSkillRecords();
          return;
        }
        
        const skillMap = {};
        skillRecords.forEach(record => {
          const key = `${record.userId}_${record.procedureId}_${record.role}`;
          skillMap[key] = skillScoreMap[record.level] || 0;
        });

        const results = procedures.map(proc => {
          let scrubInstructors = 0;
          let scrubIndependent = 0;
          let circInstructors = 0;
          let circIndependent = 0;

          staffList.forEach(staff => {
            const scrubScore = skillMap[`${staff.id}_${proc.id}_scrub`] || 0;
            const circScore = skillMap[`${staff.id}_${proc.id}_circulating`] || 0;

            if (scrubScore >= 7) scrubInstructors++;
            if (scrubScore >= 6) scrubIndependent++;
            if (circScore >= 7) circInstructors++;
            if (circScore >= 6) circIndependent++;
          });

          return {
            procedureId: proc.id,
            procedureName: proc.name,
            department: proc.department || '未分類',
            scrubInstructors,
            scrubIndependent,
            circInstructors,
            circIndependent
          };
        });

        console.log('✅ マスターデータ計算完了');
        setMasterData(results);
        setCalculating(false);
      };

      if (procedures.length > 0 && staffList.length > 0 && currentPage === 'master') {
        calculateMasterData();
      } else if (currentPage !== 'master') {
        setMasterData([]);
      }
    }, [procedures, staffList, skillRecords.length, currentPage]);

    return (
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-6">術式別スキル保有者一覧</h2>
        
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
          <div className="text-sm">
            <strong>データ状況:</strong> スタッフ {staffList.length}人 / 
            スキルレコード {skillRecords.length}件 / 
            術式 {procedures.length}件
          </div>
        </div>

        {calculating ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="animate-spin mr-3" size={32} />
            <span className="text-gray-600">データを集計しています...</span>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold" rowSpan={2}>
                    <SortButton column="department" label="診療科" />
                  </th>
                  <th className="px-4 py-3 text-left font-semibold" rowSpan={2}>術式名</th>
                  <th className="px-4 py-3 text-center font-semibold border-l" colSpan={2}>器械出し</th>
                  <th className="px-4 py-3 text-center font-semibold border-l" colSpan={2}>外回り</th>
                </tr>
                <tr>
                  <th className="px-4 py-3 text-center text-sm border-l">
                    <SortButton column="scrubInstructors" label="指導可" />
                  </th>
                  <th className="px-4 py-3 text-center text-sm">
                    <SortButton column="scrubIndependent" label="独り立ち" />
                  </th>
                  <th className="px-4 py-3 text-center text-sm border-l">
                    <SortButton column="circInstructors" label="指導可" />
                  </th>
                  <th className="px-4 py-3 text-center text-sm">
                    <SortButton column="circIndependent" label="独り立ち" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {getSortedData().map((data, idx) => (
                  <tr key={data.procedureId} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-3 text-sm text-gray-600">{data.department}</td>
                    <td className="px-4 py-3 font-medium">{data.procedureName}</td>
                    
                    <td className="px-4 py-3 text-center border-l">
                      <span className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-sm font-semibold">
                        {data.scrubInstructors}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-semibold">
                        {data.scrubIndependent}
                      </span>
                    </td>
                    
                    <td className="px-4 py-3 text-center border-l">
                      <span className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-sm font-semibold">
                        {data.circInstructors}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-semibold">
                        {data.circIndependent}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-semibold mb-2">凡例</h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="flex items-center gap-2">
              <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">数値</span>
              <span>指導可能者(レベル7)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">数値</span>
              <span>独り立ち者(レベル6以上)</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader className="animate-spin mx-auto mb-4" size={40} />
          <p className="text-gray-600">データを読み込んでいます...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow-md">
        <div className="flex">
          <button
            onClick={() => setCurrentPage('training')}
            className={`flex-1 py-4 px-6 flex items-center justify-center gap-2 ${
              currentPage === 'training' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Users size={20} />
            育成方針
          </button>
          <button
            onClick={() => setCurrentPage('schedule')}
            className={`flex-1 py-4 px-6 flex items-center justify-center gap-2 ${
              currentPage === 'schedule' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Calendar size={20} />
            人員配置
          </button>
          <button
            onClick={() => setCurrentPage('master')}
            className={`flex-1 py-4 px-6 flex items-center justify-center gap-2 ${
              currentPage === 'master' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <BarChart3 size={20} />
            マスター
          </button>
        </div>
      </nav>

      {currentPage === 'training' && <TrainingPlanPage />}
      {currentPage === 'schedule' && <SchedulePage />}
      {currentPage === 'master' && <MasterPage />}

      {selectionMode && <StaffSelectionModal />}
      {showSkillChart && <SkillChartModal staff={showSkillChart} />}
      {selectedProcedureForTraining && selectedRoleForTraining && (
        <TraineeSelectionModal 
          procedure={selectedProcedureForTraining}
          role={selectedRoleForTraining}
          onClose={() => {
            setSelectedProcedureForTraining(null);
            setSelectedRoleForTraining(null);
          }}
        />
      )}
    </div>
  );
};

export default StaffAssignmentEnhanced;